import React from 'react';
import I18n from 'i18n-js';
import { debounce } from 'lodash';
import { gql } from '@reverbdotcom/commons/src/gql';

import { PriceDisplay } from '@reverbdotcom/commons/src/components/PriceDisplay';
import { RCTooltip } from '@reverbdotcom/commons';
import { parseAmount, parseAmountCents } from '@reverbdotcom/commons/src/money';
import { CA } from '@reverbdotcom/commons/src/constants';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { RCAlertBox, RCIcon, RCLoadingBars } from '@reverbdotcom/cadence/components';
import { MParticleEventName, trackEvent, EventType } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ReverbLogoCircleIcon, InfoIcon } from '@reverbdotcom/cadence/icons/react';
import { SellingFeeLimit, Shop } from '@reverbdotcom/commons/src/gql/graphql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { filterVatCountries } from '../../../shop_settings_vat_layout';
import StringUtil from '../../../../reverb-js-common/string_util';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';

export const ESTIMATED_EARNINGS_QUERY = gql(`
  query Core_RenewListing_EstimatedEarnings(
    $listingId: String!,
    $amount: String!,
    $currency: String!
  ) {
    estimatedEarnings(input:{
      listingId: $listingId,
      listingPrice: {
        amount: $amount
        currency: $currency
      }
    }) {
      feeLineItems {
        fee {
          amount
          display
          amountCents
        }
        type
        label
        tooltipText
      }
      estimatedEarnings {
        amount
        display
        amountCents
      }
      estimatedEarningsTooltipText
    }
  }
`);

interface IProps {
  listingId?: string;
  price?: string;
  currency?: string;
  shop?: Shop;
  showIncVatPricingHint?: boolean;
  sellingFeeLimit?: SellingFeeLimit
}

enum priceKeys {
  SELLING_FEE = 'sellingFee',
  PROCESSING_FEE = 'processingFee',
  WALLET_INCENTIVE = 'walletIncentive',
  ESTIMATED_EARNINGS = 'estimatedEarnings',
}

const MINIMUM_PRICE_CENTS = 99;
const TOOLTIP_EVENT_DELAY = 3000;
const VAT_ID_HC_ARTICLE = '15478546545171';
const COMPONENT_NAME = 'PriceBreakdown';

const SELLING_FEE = 'SELLING_FEE';
const PAYMENT_PROCESSING_FEE = 'PAYMENT_PROCESSING_FEE';
const WALLET_INCENTIVE = 'WALLET_INCENTIVE';

export function formatEstimatedEarnings(priceAmountCents: number, currency: string, feesAmountCents: number[]) {
  const estimatedEarnings = feesAmountCents.reduce((acc, currentFee) => {
    return acc - currentFee;
  }, priceAmountCents);
  return parseAmountCents(estimatedEarnings, currency);
}

function isVatCountry(shop: Shop) {
  const address = shop?.address ?? {};

  return !!filterVatCountries([{
    countryCode: address.countryCode,
  }]).length || address.countryCode === CA;
}

function formatTooltipText(text: string | undefined): React.ReactNode {
  if (!text) return null;
  return <span style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>{text}</span>;
}

function getTooltipText(field, estimatedEarningsResponse): React.ReactNode {
  switch (field) {
    case priceKeys.SELLING_FEE:
      return formatTooltipText(estimatedEarningsResponse?.estimatedEarnings.feeLineItems.find(feeLineItem => feeLineItem.type === SELLING_FEE)?.tooltipText);

    case priceKeys.PROCESSING_FEE:
      return formatTooltipText(estimatedEarningsResponse?.estimatedEarnings.feeLineItems.find(feeLineItem => feeLineItem.type === PAYMENT_PROCESSING_FEE)?.tooltipText);

    case priceKeys.WALLET_INCENTIVE:
      return formatTooltipText(estimatedEarningsResponse?.estimatedEarnings.feeLineItems.find(feeLineItem => feeLineItem.type === WALLET_INCENTIVE)?.tooltipText);

    case priceKeys.ESTIMATED_EARNINGS:
      return formatTooltipText(estimatedEarningsResponse?.estimatedEarnings.estimatedEarningsTooltipText);

    default:
      return null;
  }
}

function getFeeAmountCentsByType(estimatedEarningsResponse, type) {
  return estimatedEarningsResponse?.estimatedEarnings.feeLineItems.find(feeLineItem => feeLineItem.type === type)?.fee.amountCents ?? 0;
}

function toolTipOpenEvent(open: boolean, tooltipName: string) {
  if (!open) return;

  trackEvent({
    eventName: MParticleEventName.TooltipOpened,
    eventType: EventType.Other,
    componentName: COMPONENT_NAME,
    source: tooltipName,
  });
}

export function PriceBreakdown({
  listingId,
  price,
  currency,
  shop,
  showIncVatPricingHint,
}: IProps) {
  const priceFormatted = parseAmount(price, currency);
  const priceAmountCents = priceFormatted.amountCents;
  const { data: estimatedEarningsResponse, loading, errors } = useQuery(
    ESTIMATED_EARNINGS_QUERY,
    {
      variables: { listingId, amount: price, currency },
      skip: !price || !currency || priceAmountCents <= MINIMUM_PRICE_CENTS,
    },
  );

  const vatIdHcArticleUrl = useHelpCenterArticleUrl(VAT_ID_HC_ARTICLE);
  const debouncedTooltipOpenEvent = React.useCallback(debounce((open, tooltipName) => {
    toolTipOpenEvent(open, tooltipName);
  }, TOOLTIP_EVENT_DELAY), []);

  if (loading) return <RCLoadingBars />;
  if (!price || !currency || priceAmountCents <= MINIMUM_PRICE_CENTS || errors) return null;

  const processingFeeAmountCents = getFeeAmountCentsByType(estimatedEarningsResponse, PAYMENT_PROCESSING_FEE);
  const sellingFeeAmountCents = getFeeAmountCentsByType(estimatedEarningsResponse, SELLING_FEE);
  const walletIncentiveAmountCents = getFeeAmountCentsByType(estimatedEarningsResponse, WALLET_INCENTIVE);

  const estimatedEarningsAmountCents = estimatedEarningsResponse?.estimatedEarnings.estimatedEarnings.amountCents;

  const taxIncludedHint = showIncVatPricingHint ? I18n.t('discovery.sellForm.pricingSection.priceBreakdown.estimatedEarnings.specifier') : '';
  const showVatIdHcArticle = isVatCountry(shop) && !shop?.vatId;

  if (estimatedEarningsAmountCents <= 0) return null;

  const priceRows: { [key in priceKeys]: {
    amountCents: number;
    specifier: string;
    modifier: string;
    icon?: JSX.Element;
  } } = {
    [priceKeys.SELLING_FEE]: {
      amountCents: sellingFeeAmountCents,
      specifier: '',
      modifier: '- ',
      icon: <RCIcon svgComponent={InfoIcon} size="inline" />,
    },
    [priceKeys.PROCESSING_FEE]: {
      amountCents: processingFeeAmountCents,
      specifier: '',
      modifier: '- ',
      icon: <RCIcon svgComponent={InfoIcon} size="inline" />,
    },
    [priceKeys.WALLET_INCENTIVE]: {
      amountCents: walletIncentiveAmountCents,
      specifier: '',
      modifier: '+ ',
      icon: <ReverbLogoCircleIcon className="reverb-logo-circle-icon" />,
    },
    [priceKeys.ESTIMATED_EARNINGS]: {
      amountCents: estimatedEarningsAmountCents,
      specifier: taxIncludedHint,
      modifier: '',
      icon: <RCIcon svgComponent={InfoIcon} size="inline" />,
    },
  };

  return (
    <>
      <table className="price-breakdown__table">
        <tbody>
          {Object.entries(priceRows).map(([field, { amountCents, specifier, modifier, icon }]) => {
            if (amountCents === 0) return null;

            return (
              <tr key={field} id={StringUtil.camelCaseToKebabCase(field)}>
                <td>
                  <RCTooltip
                    text={getTooltipText(field, estimatedEarningsResponse)}
                    onOpenChange={(open) => debouncedTooltipOpenEvent(open, field)}
                  >
                    <span>
                      <I18N
                        html
                        text={`discovery.sellForm.pricingSection.priceBreakdown.${field}.label`}
                        args={{
                          specifier: specifier,
                        }}
                      >
                        {field === priceKeys.WALLET_INCENTIVE ? icon : null}
                      </I18N>
                      {field !== priceKeys.WALLET_INCENTIVE ? icon : null}
                    </span>
                  </RCTooltip>
                </td>
                <td className="price-breakdown__value">
                  <PriceDisplay
                    display={modifier + parseAmountCents(amountCents, currency).display}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {showVatIdHcArticle &&
        <div className="price-breakdown__vat-id-alert">
          <RCAlertBox type="warning" small>
            <I18N
              html
              text="discovery.sellForm.pricingSection.priceBreakdown.vatIdAlert"
              args={{ linkOpen: `<br /><a href="${vatIdHcArticleUrl}">`, linkClose: '</a>' }}
            />
          </RCAlertBox>
        </div>
      }
    </>
  );
}

export default PriceBreakdown;
